import React, { useState, useMemo } from 'react';
import { useTimeTrackerStore } from '@/stores/time-tracker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { TimeTrackerProject, TimeTrackerClient, TimeTrackerTask } from '@/types/pm-types';
import { pmTheme } from './pmTheme';

type ManagerTab = 'projects' | 'clients' | 'tasks';

interface TimeTrackerProjectManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accentColor?: string;
  currency?: string;
}

const PROJECT_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#06b6d4',
];

function generateId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export const TimeTrackerProjectManager: React.FC<TimeTrackerProjectManagerProps> = ({
  open,
  onOpenChange,
  accentColor = '#10b981',
  currency = 'USD',
}) => {
  const [activeTab, setActiveTab] = useState<ManagerTab>('projects');

  // Store
  const projects = useTimeTrackerStore((s) => s.projects);
  const clients = useTimeTrackerStore((s) => s.clients);
  const tasks = useTimeTrackerStore((s) => s.tasks);
  const addProject = useTimeTrackerStore((s) => s.addProject);
  const updateProject = useTimeTrackerStore((s) => s.updateProject);
  const deleteProject = useTimeTrackerStore((s) => s.deleteProject);
  const addClient = useTimeTrackerStore((s) => s.addClient);
  const updateClient = useTimeTrackerStore((s) => s.updateClient);
  const deleteClient = useTimeTrackerStore((s) => s.deleteClient);
  const addTask = useTimeTrackerStore((s) => s.addTask);
  const updateTask = useTimeTrackerStore((s) => s.updateTask);
  const deleteTask = useTimeTrackerStore((s) => s.deleteTask);

  // Editing states
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [newProject, setNewProject] = useState<Partial<TimeTrackerProject> | null>(null);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [newClient, setNewClient] = useState<Partial<TimeTrackerClient> | null>(null);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [newTask, setNewTask] = useState<Partial<TimeTrackerTask> | null>(null);

  // Tasks grouped by project
  const tasksByProject = useMemo(() => {
    const map = new Map<string, TimeTrackerTask[]>();
    for (const t of tasks) {
      const list = map.get(t.projectId) || [];
      list.push(t);
      map.set(t.projectId, list);
    }
    return map;
  }, [tasks]);

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px', border: `1px solid ${pmTheme.input}`, borderRadius: 6,
    fontSize: 13, outline: 'none', width: '100%', color: pmTheme.text, background: pmTheme.surface,
  };

  const btnStyle = (primary = false): React.CSSProperties => ({
    padding: '5px 14px', border: primary ? 'none' : `1px solid ${pmTheme.input}`,
    borderRadius: 6, background: primary ? accentColor : pmTheme.surface,
    color: primary ? pmTheme.textInverse : pmTheme.text, fontSize: 12, fontWeight: 500,
    cursor: 'pointer',
  });

  // ─── Project Form ─────────────────────────────────────
  const renderProjectForm = (proj: Partial<TimeTrackerProject>, isNew: boolean) => {
    const update = (field: string, value: unknown) => {
      if (isNew) setNewProject((p) => ({ ...p, [field]: value }));
      else if (editingProjectId) updateProject(editingProjectId, { [field]: value } as Partial<TimeTrackerProject>);
    };
    const save = () => {
      if (isNew && proj.name) {
        addProject({
          id: generateId('proj'),
          name: proj.name,
          clientId: proj.clientId,
          color: proj.color || '#3b82f6',
          billableRate: proj.billableRate || 0,
          currency: proj.currency || currency,
          isBillable: proj.isBillable ?? true,
        });
        setNewProject(null);
      } else {
        setEditingProjectId(null);
      }
    };
    const cancel = () => { if (isNew) setNewProject(null); else setEditingProjectId(null); };

    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', flexWrap: 'wrap' }}>
        <input placeholder="Project name" value={proj.name || ''} onChange={(e) => update('name', e.target.value)} style={{ ...inputStyle, flex: 2, minWidth: 140 }} />
        <select value={proj.clientId || ''} onChange={(e) => update('clientId', e.target.value || undefined)} style={{ ...inputStyle, flex: 1, minWidth: 100 }}>
          <option value="">No client</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 3 }}>
          {PROJECT_COLORS.map((c) => (
            <button key={c} onClick={() => update('color', c)} style={{
              width: 20, height: 20, borderRadius: '50%', background: c, border: c === (proj.color || '#3b82f6') ? `2px solid ${pmTheme.text}` : '2px solid transparent', cursor: 'pointer', padding: 0,
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 12, color: pmTheme.textMuted }}>$/hr</span>
          <input type="number" min={0} value={proj.billableRate ?? 0} onChange={(e) => update('billableRate', Number(e.target.value))} style={{ ...inputStyle, width: 80 }} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: pmTheme.textMuted, cursor: 'pointer' }}>
          <input type="checkbox" checked={proj.isBillable ?? true} onChange={(e) => update('isBillable', e.target.checked)} />
          Billable
        </label>
        <button onClick={save} style={btnStyle(true)}>Save</button>
        <button onClick={cancel} style={btnStyle()}>Cancel</button>
      </div>
    );
  };

  // ─── Client Form ──────────────────────────────────────
  const renderClientForm = (client: Partial<TimeTrackerClient>, isNew: boolean) => {
    const update = (field: string, value: unknown) => {
      if (isNew) setNewClient((c) => ({ ...c, [field]: value }));
      else if (editingClientId) updateClient(editingClientId, { [field]: value } as Partial<TimeTrackerClient>);
    };
    const save = () => {
      if (isNew && client.name) {
        addClient({
          id: generateId('client'),
          name: client.name,
          color: client.color || '#3b82f6',
          contactEmail: client.contactEmail,
          address: client.address,
        });
        setNewClient(null);
      } else {
        setEditingClientId(null);
      }
    };
    const cancel = () => { if (isNew) setNewClient(null); else setEditingClientId(null); };

    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', flexWrap: 'wrap' }}>
        <input placeholder="Client name" value={client.name || ''} onChange={(e) => update('name', e.target.value)} style={{ ...inputStyle, flex: 2, minWidth: 140 }} />
        <input placeholder="Contact email" value={client.contactEmail || ''} onChange={(e) => update('contactEmail', e.target.value)} style={{ ...inputStyle, flex: 2, minWidth: 160 }} />
        <input placeholder="Address" value={client.address || ''} onChange={(e) => update('address', e.target.value)} style={{ ...inputStyle, flex: 2, minWidth: 160 }} />
        <button onClick={save} style={btnStyle(true)}>Save</button>
        <button onClick={cancel} style={btnStyle()}>Cancel</button>
      </div>
    );
  };

  // ─── Task Form ────────────────────────────────────────
  const renderTaskForm = (task: Partial<TimeTrackerTask>, isNew: boolean) => {
    const update = (field: string, value: unknown) => {
      if (isNew) setNewTask((t) => ({ ...t, [field]: value }));
      else if (editingTaskId) updateTask(editingTaskId, { [field]: value } as Partial<TimeTrackerTask>);
    };
    const save = () => {
      if (isNew && task.name && task.projectId) {
        addTask({
          id: generateId('task'),
          projectId: task.projectId,
          name: task.name,
          billableRate: task.billableRate,
        });
        setNewTask(null);
      } else {
        setEditingTaskId(null);
      }
    };
    const cancel = () => { if (isNew) setNewTask(null); else setEditingTaskId(null); };

    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 0', flexWrap: 'wrap' }}>
        <select value={task.projectId || ''} onChange={(e) => update('projectId', e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 120 }}>
          <option value="">Select project</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input placeholder="Task name" value={task.name || ''} onChange={(e) => update('name', e.target.value)} style={{ ...inputStyle, flex: 2, minWidth: 140 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontSize: 12, color: pmTheme.textMuted }}>Rate override</span>
          <input type="number" min={0} value={task.billableRate ?? ''} onChange={(e) => update('billableRate', e.target.value ? Number(e.target.value) : undefined)} placeholder="—" style={{ ...inputStyle, width: 80 }} />
        </div>
        <button onClick={save} style={btnStyle(true)}>Save</button>
        <button onClick={cancel} style={btnStyle()}>Cancel</button>
      </div>
    );
  };

  const TABS: { key: ManagerTab; label: string }[] = [
    { key: 'projects', label: 'Projects' },
    { key: 'clients', label: 'Clients' },
    { key: 'tasks', label: 'Tasks' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-3">
          <DialogTitle className="text-base">Manage Projects & Clients</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${pmTheme.border}` }}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1, padding: '10px 16px', border: 'none',
                borderBottom: activeTab === tab.key ? `2px solid ${accentColor}` : '2px solid transparent',
                background: 'transparent', color: activeTab === tab.key ? accentColor : pmTheme.textMuted,
                fontWeight: activeTab === tab.key ? 600 : 400, fontSize: 13, cursor: 'pointer',
              }}
            >
              {tab.label} ({tab.key === 'projects' ? projects.length : tab.key === 'clients' ? clients.length : tasks.length})
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ overflow: 'auto', padding: '12px 20px', maxHeight: '60vh' }}>
          {/* Projects Tab */}
          {activeTab === 'projects' && (
            <div>
              <button onClick={() => setNewProject({ color: '#3b82f6', isBillable: true, billableRate: 0, currency })} style={{ ...btnStyle(true), marginBottom: 12 }}>
                + Add Project
              </button>
              {newProject && renderProjectForm(newProject, true)}
              {projects.map((p) => (
                <div key={p.id} style={{ borderBottom: `1px solid ${pmTheme.borderSubtle}`, padding: '10px 0' }}>
                  {editingProjectId === p.id ? (
                    renderProjectForm(p, false)
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ width: 14, height: 14, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: pmTheme.text }}>{p.name}</span>
                      {p.clientId && <span style={{ fontSize: 12, color: pmTheme.textSubtle }}>{clients.find((c) => c.id === p.clientId)?.name}</span>}
                      <span style={{ fontSize: 12, color: p.isBillable ? accentColor : pmTheme.textSubtle, fontFamily: 'monospace' }}>
                        {p.isBillable ? `$${p.billableRate}/hr` : 'non-billable'}
                      </span>
                      <button onClick={() => setEditingProjectId(p.id)} style={btnStyle()}>Edit</button>
                      <button onClick={() => deleteProject(p.id)} style={{ ...btnStyle(), color: pmTheme.danger, borderColor: pmTheme.dangerBorder }}>Delete</button>
                    </div>
                  )}
                </div>
              ))}
              {projects.length === 0 && !newProject && (
                <div style={{ padding: 24, textAlign: 'center', color: pmTheme.textSubtle, fontSize: 13 }}>No projects yet. Click "+ Add Project" to create one.</div>
              )}
            </div>
          )}

          {/* Clients Tab */}
          {activeTab === 'clients' && (
            <div>
              <button onClick={() => setNewClient({})} style={{ ...btnStyle(true), marginBottom: 12 }}>
                + Add Client
              </button>
              {newClient && renderClientForm(newClient, true)}
              {clients.map((c) => (
                <div key={c.id} style={{ borderBottom: `1px solid ${pmTheme.borderSubtle}`, padding: '10px 0' }}>
                  {editingClientId === c.id ? (
                    renderClientForm(c, false)
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: pmTheme.text }}>{c.name}</span>
                      {c.contactEmail && <span style={{ fontSize: 12, color: pmTheme.textSubtle }}>{c.contactEmail}</span>}
                      <button onClick={() => setEditingClientId(c.id)} style={btnStyle()}>Edit</button>
                      <button onClick={() => deleteClient(c.id)} style={{ ...btnStyle(), color: pmTheme.danger, borderColor: pmTheme.dangerBorder }}>Delete</button>
                    </div>
                  )}
                </div>
              ))}
              {clients.length === 0 && !newClient && (
                <div style={{ padding: 24, textAlign: 'center', color: pmTheme.textSubtle, fontSize: 13 }}>No clients yet. Click "+ Add Client" to create one.</div>
              )}
            </div>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div>
              <button onClick={() => setNewTask({})} style={{ ...btnStyle(true), marginBottom: 12 }}>
                + Add Task
              </button>
              {newTask && renderTaskForm(newTask, true)}
              {projects.map((p) => {
                const projectTasks = tasksByProject.get(p.id) || [];
                if (projectTasks.length === 0) return null;
                return (
                  <div key={p.id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                      <span style={{ width: 10, height: 10, borderRadius: '50%', background: p.color }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: pmTheme.text }}>{p.name}</span>
                    </div>
                    {projectTasks.map((t) => (
                      <div key={t.id} style={{ borderBottom: `1px solid ${pmTheme.borderSubtle}`, padding: '6px 0 6px 20px' }}>
                        {editingTaskId === t.id ? (
                          renderTaskForm(t, false)
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ flex: 1, fontSize: 13, color: pmTheme.text }}>{t.name}</span>
                            {t.billableRate !== undefined && (
                              <span style={{ fontSize: 12, color: accentColor, fontFamily: 'monospace' }}>${t.billableRate}/hr</span>
                            )}
                            <button onClick={() => setEditingTaskId(t.id)} style={btnStyle()}>Edit</button>
                            <button onClick={() => deleteTask(t.id)} style={{ ...btnStyle(), color: pmTheme.danger, borderColor: pmTheme.dangerBorder }}>Delete</button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
              {tasks.length === 0 && !newTask && (
                <div style={{ padding: 24, textAlign: 'center', color: pmTheme.textSubtle, fontSize: 13 }}>No tasks yet. Click "+ Add Task" to create one.</div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

TimeTrackerProjectManager.displayName = 'TimeTrackerProjectManager';
